/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import pluralize from '../../utils/pluralize';
import { CheckboxProps } from './__fixtures__';
import Button from '../button';

const LinkButton = styled(Button)`
  text-transform: none;
  text-decoration: underline;
`;

const HeaderListItemText = styled(ListItemText)`
  flex-grow: 0;
`;

type SelectionContext = 'underPageLimit' | 'overPageLimit' | 'searchUnderPageLimit' | 'searchOverPageLimit';
type SelectionState = 'allExisting' | 'allInPage' | 'some' | 'none';

const selectionTextFormatters = {
  underPageLimit: {
    allExisting: (count: number) => `${count.toLocaleString()} selected`,
    allInPage: (count: number) => `${count.toLocaleString()} selected`,
    some: (count: number) => `${count.toLocaleString()} selected`,
    none: (_count: number, total: number) => `${pluralize(total, 'item')}`,
  },
  overPageLimit: {
    allExisting: (_count: number, total: number) => `All ${pluralize(total, 'item')} selected`,
    allInPage: (count: number) => `${count.toLocaleString()} selected`,
    some: (count: number) => `${count.toLocaleString()} selected`,
    none: (_count: number, total: number, perPage: number) => `${perPage.toLocaleString()} of ${total.toLocaleString()} items`,
  },
  searchUnderPageLimit: {
    allExisting: (count: number) => `${pluralize(count, 'result')} selected`,
    allInPage: (count: number) => `${pluralize(count, 'result')} selected`,
    some: (count: number) => `${pluralize(count, 'result')} selected`,
    none: (_count: number, total: number) => `${pluralize(total, 'result')}`,
  },
  searchOverPageLimit: {
    allExisting: (_count: number, total: number) => `All ${pluralize(total, 'result')} selected`,
    allInPage: (count: number) => `${pluralize(count, 'result')}  selected`,
    some: (count: number) => `${pluralize(count, 'result')}  selected`,
    none: (_count: number, total: number, perPage: number) => `${perPage.toLocaleString()} of ${total.toLocaleString()} results`,
  },
};

function getSelectionContext(isSearching, isUnderPageLimit): SelectionContext {
  if (isSearching && isUnderPageLimit) {
    return 'searchUnderPageLimit';
  }
  if (isSearching && !isUnderPageLimit) {
    return 'searchOverPageLimit';
  }
  if (!isSearching && isUnderPageLimit) {
    return 'underPageLimit';
  }
  return 'overPageLimit';
}

function getSelectionState(length, isSelectAll, hasAllExisting): SelectionState {
  if (hasAllExisting) {
    return 'allExisting';
  }
  if (isSelectAll) {
    return 'allInPage';
  }
  if (length > 0) {
    return 'some';
  }
  return 'none';
}

type FormatSelectionTextParms = {
  length: number;
  allExistingCount: number;
  pageLimit: number;
  isSelectAll: boolean;
  hasAllExisting: boolean;
  isSearching?: boolean;
}
function formatSelectionText({
  length,
  allExistingCount,
  pageLimit,
  isSelectAll,
  hasAllExisting,
  isSearching,
}: FormatSelectionTextParms): string {
  const isUnderPageLimit = allExistingCount <= pageLimit;
  const selectionContext = getSelectionContext(isSearching, isUnderPageLimit);
  const selectionState = getSelectionState(length, isSelectAll, hasAllExisting);
  const getSelectionText = selectionTextFormatters[selectionContext][selectionState];
  const selectionText = getSelectionText(
    length,
    allExistingCount,
    pageLimit,
  );
  return selectionText;
}

interface Props {
  length: number;
  isSearching?: boolean;
  isSelectAll: boolean;
  onSelectAll: () => void;
  selectAllExisting: () => void;
  unselectAllExisting: () => void;
  allExistingCount: number;
  hasAllExisting: boolean;
  pageLimit: number;
  hideSelectAll?: boolean;
  selectionLimit?: number;
  sortOrder?: 'asc' | 'desc';
  onSort?: () => void;
}

export default function MenuListHeader(props: Props) {
  const {
    length,
    isSearching,
    isSelectAll,
    selectAllExisting,
    unselectAllExisting,
    onSelectAll,
    allExistingCount,
    hasAllExisting,
    pageLimit,
    hideSelectAll,
    selectionLimit,
    sortOrder,
    onSort,
  } = props;
  const handleSelectAllExisting = (event) => {
    event.stopPropagation();
    selectAllExisting();
  };

  const handleClearSelection = (event) => {
    event.stopPropagation();
    unselectAllExisting();
  };

  const primaryText = formatSelectionText({
    length,
    allExistingCount,
    pageLimit,
    isSelectAll,
    hasAllExisting,
    isSearching,
  });

  const displayText = selectionLimit != null && length > 0
    ? `${length} of ${selectionLimit} selected`
    : primaryText;

  if (hideSelectAll) {
    const getSortIcon = () => {
      if (sortOrder === 'desc') return <ArrowDownwardIcon fontSize="small" />;
      return <ArrowUpwardIcon fontSize="small" />;
    };

    const getSortLabel = () => {
      if (sortOrder === 'desc') return 'Z-A';
      return 'A-Z';
    };

    return (
      <>
        <ListSubheader component={ListItem} sx={{ p: 0 }}>
          <Box
            component="div"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              px: 2,
              py: 1,
              minHeight: 48,
            }}
          >
            <HeaderListItemText primary={displayText} />
            {onSort && (
              <Button
                onClick={onSort}
                startIcon={getSortIcon()}
                size="small"
                sx={{ textTransform: 'none', minWidth: 'auto' }}
              >
                SORT BY:
                {' '}
                {getSortLabel()}
              </Button>
            )}
          </Box>
        </ListSubheader>
        <Divider sx={{ borderWidth: 2 }} />
      </>
    );
  }

  return (
    <>
      <ListSubheader component={ListItem} sx={{ p: 0 }}>
        <ListItemButton onClick={onSelectAll} dense style={{ justifyContent: 'flex-start' }}>
          <ListItemIcon sx={{ minWidth: 0 }}>
            <Checkbox
              checked={isSelectAll || hasAllExisting}
              indeterminate={!(hasAllExisting || isSelectAll) && length > 0}
              inputProps={{ 'aria-label': 'select all' }}
              {...CheckboxProps}
            />
          </ListItemIcon>
          <HeaderListItemText primary={primaryText} />
          {isSelectAll && (allExistingCount > pageLimit) && !hasAllExisting && (
            <LinkButton color="info" onClick={handleSelectAllExisting}>
              Select all
              {' '}
              {allExistingCount}
              {' '}
              items
            </LinkButton>
          )}
          {hasAllExisting && (
            <LinkButton color="info" onClick={handleClearSelection}>
              Clear selection
            </LinkButton>
          )}
        </ListItemButton>
      </ListSubheader>
      <Divider sx={{ borderWidth: 2 }} />
    </>
  );
}
